import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { intervalNoteMidi, ladderHomeNote } from '../../../../../common/src/interval_training.ts';
import {
  clefForVoicePart,
  currentStreak,
  learnLevelName,
  learnModuleName,
  markSyllablesMet,
  moduleProgress,
  needsSyllables,
  RecordedSession,
  recordLearnSession,
  voicePartForRange,
} from '../../../../../common/src/learn_rules.ts';
import { LearnLevel, LearnModule, LearnProgress, VocalRange } from '../../../../../common/src/model.ts';
import { getLearnPath, LearnPages } from '../../../../../common/src/pages.ts';
import { RandomSource } from '../../../../../common/src/rhythm_echo.ts';
import {
  pickSolfegePhrases,
  SolfegeLevel,
  solfegeNoteMidi,
  solfegePhrasesPerSession,
} from '../../../../../common/src/sing_in_solfege.ts';
import { LocalStorageKey } from '../../../data/client_local_storage.ts';
import { LearnProgressUpdate, useLearnProgress } from '../../../data/use_learn_progress.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { OuterPageContent } from '../../../util/shared.tsx';
import { useCurrentDate } from '../../../util/use_current_date.tsx';
import { Header } from '../../header.tsx';
import { CaptureSlice } from '../audio/capture_slice.ts';
import { getLearnAudioSession, LearnAudioSession, PianoNote, softGuideGain } from '../audio/learn_audio_session.ts';
import { MicFailure, micFailureOf } from '../audio/learn_mic_capture.ts';
import { useAudioClock } from '../audio/use_audio_clock.ts';
import { intervalSoundLeadMs, playSoloNote } from '../interval/interval_audio.ts';
import { nearStaffMidiRange } from '../interval/interval_staff_geometry.ts';
import { MeetSyllablesScreen } from '../interval/meet_syllables_screen.tsx';
import { learnModuleBack } from '../learn_classes.ts';
import { nowOn } from '../learn_dates.ts';
import { failureScreenIds, SingingFailureScreen } from '../learn_failure_screen.tsx';
import { learnHandedOnState, useLearnExit, useLearnFrom } from '../learn_navigation.tsx';
import { LearnSummaryScreen, summaryScreenIds } from '../learn_summary_screen.tsx';
import { SolfegePhraseScreen } from './solfege_phrase_screen.tsx';
import { placeSolfegePhrases } from './solfege_placement.ts';
import { keptTakeOf } from './solfege_playback.ts';
import {
  currentSolfegePhrase,
  estimatorTargetMidi,
  initialSolfegeSessionState,
  isSolfegeHearingMine,
  isSolfegePhrasePlaying,
  reduceSolfegeSession,
  solfegeClockRunning,
  SolfegePhase,
  SolfegeSessionAction,
  SolfegeSessionState,
  SolfegeSoundPlan,
  solfegeSoundPlan,
  solfegeSummaryScore,
  solfegeTakeSpan,
} from './solfege_session.ts';
import { SolfegeStartScreen } from './solfege_start_screen.tsx';
import { solfegeSummaryProps } from './solfege_summary.ts';

// Sing in Solfège (issue #3722): the page behind the Pitch class screen's third tile. It is
// Interval Training's page (interval_training_page.tsx) with phrases for patterns, and keeps its
// shape piece by piece: the range gate, Meet the syllables on a first visit, the start screen, one
// screen for the phrases of a four-phrase session - listen, sing in tempo, the result - and the
// shared failure and summary screens. No footer.
//
// The ways out are Interval Training's. The header's back arrow closes an open session (listen, sing,
// result, the summary or the failure screen) back to the start screen, closes the reopened syllables
// back to it too, and only from the start screen, or a first visit's syllables, leaves for the Pitch
// class screen. The summary's and the failure screen's "Back to Pitch" leave for the class screen.
// A session is not a history entry, so the hardware and browser back buttons leave the page from
// anywhere. Leaving, or closing a session, mid-way records nothing and keeps nothing.
//
// The range gate. Every phrase is moved into the singer's measured range, so a member without one is
// sent to the Range Finder, the redirect handing on where they came from and that they came for Sing
// in Solfège, so the Range Finder's result leads back here (learn_navigation.tsx).
//
// The syllables gate. With no session open the page shows Meet the syllables while the member has not
// met them (needsSyllables) and the start screen otherwise. "Got it, let's start" records the meeting
// once; "How to say the syllables" on the start screen reopens it, and from there both ways back
// return to the start screen and write nothing. A rung plays its syllable from the ladder's do, as
// Interval Training's does.
//
// A session. Start, and "Play again" on the summary, open the mic first - the tap is the gesture -
// then build the session: four phrases of the level's deck that fit the range, openings first, then
// last phrases and lines from the middle of a hymn joining them as the singer's qualifying sessions
// at the level mount up, each kind moving on with the sessions played (pickSolfegePhrases), each
// with a do of its own near the singer's staff and with every note inside their saved range
// (placeSolfegePhrases), each do other than the one before where the range allows. The session runs
// in the guide mode of this module's own sing-along choice, which the start screen's switch shows
// and writes: stored per device under LocalStorageKey.SolfegeSingAlong (never on the household
// member: like Pitch Match's choice it is a property of the hardware, not of the singer), and off
// until the singer turns it on, on every device. Pitch Match's choice (pitch_mode.ts) defaults to
// on where the echo canceller is full-duplex, but a phrase playing under a whole take asks more of
// the phone's voice processing than one held note does: on an iPhone the guide cut the singer in
// and out, notes went unheard, the line broke and Hear mine had gaps, while with the guide off it
// worked well (#3722, round 2). So the two modules keep separate choices, and this one starts off.
// A mic that refused opens the session on the failure screen, so Try again resumes it.
//
// Sounds. The machine asks for one sound at a time (solfege_session.ts) and the page plays each
// request once by id, from solfegeSoundPlan: the piano notes as one run a lead ahead of the clock,
// the soft ones (the sing-along guide under a take) at softGuideGain, a click at each of the count-
// in's offsets from the run's real anchor, and for "Hear mine" the kept take through playRecording.
// "Hear guide" on the result asks for the listen screen's own 'phrase' sound, so it is played the
// same way: full gain, no clicks.
// Once it is scheduled the page reports `soundStarted` with the time the singer hears it start: the
// anchor (later than asked after a slow soundfont load) plus the output's lag (heardLagMs), so the
// take, the lit notes and the dot follow what the singer heard. A sound that cannot be played is
// logged and reported as starting at the clock's reading, so the session carries on without it. A
// request that goes away or is replaced - "Sing it", Start over, Sing again, Stop, an interruption,
// a failure, the summary - stops the piano and the recording and cancels the clicks still to come
// (the plan's "Stale sounds"), so no click of an abandoned count-in sounds over the next one. The
// known-target estimator follows the note being sung (estimatorTargetMidi), unaimed otherwise.
//
// Hear mine. While a session is open (until its summary) the rolling capture is on; it is off again
// on the summary, on closing the session and on leaving the page, and each of those drops what it
// kept (learn_audio_session.ts). The capture worklet posts the microphone in chunks of 8192 samples,
// so when the take ends on the clock the capture can be up to ~190 ms behind it. So the page waits
// solfegeCaptureCatchUpMs on the audio clock past the take's end - the result's guard keeps the clock
// running and Next phrase, which ends the mic on the last phrase, out of reach for longer than that
// (solfegeResultGuardMs) - and only then takes the capture, cuts the take's span from it
// (solfegeTakeSpan, sliceCapture), makes it loud enough to hear (solfege_playback.ts), and keeps it
// in memory, here and nowhere else: nothing is written to storage or sent anywhere. "Hear mine" is
// offered once a take is kept, and a take that could not be cut has none. Playing it passes the part
// of the take it holds (the capture may not have reached all the way back), so "Stop" and the
// playhead last exactly as long as the sound. The kept take is dropped as the result goes - Sing
// again, Next phrase, the summary, a failure - and with the session and the page.
//
// Tapping a syllable. On the listen screen, with the phrase not playing, a syllable under the staff
// plays its note on the piano on its own (playSoloNote, as the syllables' rungs play), and the
// session does not change: the next sound the session asks for, or Sing it, silences it.
//
// Interruptions. The page hidden (the document's visibility, which the Learn audio session follows
// too), or the mic found closed while the clock runs through a count-in or a take, is `interrupted`:
// the take is abandoned and the phrase goes back to listen, idle; anywhere else it only silences the
// sound playing. "Sing it", Start over and Sing again find the mic open again before they start a
// count-in, and a mic that will not open shows the failure screen.
//
// Recording. Reaching the summary records the session once, under LearnModule.SingInSolfege at Easy,
// with the session score, exactly as Interval Training records, then ends the mic. Medium is not
// built, so the summary does not announce a level unlocked (see playableUnlock below), and the
// module stays at Easy when Medium unlocks (recordLearnSession's playableLevels), so the Pitch
// screen's tile does not say Medium over a page that plays Easy.
//
// Injection. `audio` defaults to the page-wide Learn audio session and `random` to Math.random;
// tests pass the fake session and a fixed source. `phraseCount` shortens a session for a test.
// `initialState` opens on a session built by hand.

export const singInSolfegePageId = 'sing-in-solfege-page';

// "Back to Pitch" and the Pitch class screen it leads to, resolved on use, not at import
const solfegeBack = () => learnModuleBack(LearnModule.SingInSolfege);

// The exercise's own prefix for the shared summary and failure screens, and the ids they render
const solfegeIdPrefix = 'solfege';
const summaryIds = summaryScreenIds(solfegeIdPrefix);
export const solfegeSummaryScreenId = summaryIds.screen;
export const solfegeSummaryScoreTestId = summaryIds.score;
export const solfegeSummarySquareTestId = summaryIds.square;
export const solfegeSummaryUnlockedTestId = summaryIds.unlocked;
export const solfegePlayAgainButtonId = summaryIds.playAgain;
export const solfegeBackToLearnButtonId = summaryIds.backToLearn;
const failureIds = failureScreenIds(solfegeIdPrefix);
export const solfegeFailureScreenId = failureIds.screen;
export const solfegeRetryButtonId = failureIds.retry;
export const solfegeFailureBackButtonId = failureIds.back;

// How long past the take's end, on the audio clock, the page waits before cutting the take from the
// rolling capture: the worklet posts audio in 8192-sample chunks, ~171 ms at 48 kHz and ~186 ms at
// 44.1 kHz, so the capture can be that far behind the clock; the rest is the message's own delay.
// Shorter than the result's guard (solfegeResultGuardMs), which keeps the clock running until then.
export const solfegeCaptureCatchUpMs = 250;

// What the page shows, on its root's data-phase
export type SolfegePagePhase = 'syllables' | 'start' | SolfegePhase;

// The one level this build plays; Medium and Hard are not built yet
const solfegeLevel: SolfegeLevel = LearnLevel.Easy;
const selectableLevels: LearnLevel[] = [solfegeLevel];

// A level a session unlocked, announced on the summary only when it can be played. Interval
// Training's Easy-only build announced "Medium unlocked!" with no Medium to play; here the summary
// says nothing until the level exists, while the unlock itself is still recorded, so it is there
// for the singer when Medium is built.
function playableUnlock(level: LearnLevel | undefined): LearnLevel | undefined {
  return level !== undefined && selectableLevels.includes(level) ? level : undefined;
}

interface Props {
  audio?: LearnAudioSession;
  random?: RandomSource;
  initialState?: SolfegeSessionState; // opens on the session, on this state
  phraseCount?: number; // phrases per session; a test may shorten it
}

export const SingInSolfegePage = (props: Props) => {
  const { progress, updateProgress, flush } = useLearnProgress();
  const from = useLearnFrom();
  const range = progress.range;

  // No measured range, nowhere to put the phrases: the Range Finder comes first
  if (!range) {
    return (
      <Navigate
        to={getLearnPath(LearnPages.Range)}
        replace
        state={learnHandedOnState(from, getLearnPath(LearnPages.SingInSolfege))}
      />
    );
  }

  return (
    <SingInSolfegeExercise {...props} range={range} progress={progress} updateProgress={updateProgress} flush={flush} />
  );
};

// What the page holds of an open session: its state, or none until a tap opens one
interface SolfegePageState {
  session?: SolfegeSessionState;
}

// Every action but `open` and `close` belongs to the pure reducer; those two are the page's own,
// because a session is built outside the machine (beginSession) and closed back to the start screen
type SolfegePageAction = SolfegeSessionAction | { type: 'open'; state: SolfegeSessionState } | { type: 'close' };

function reduceSolfegePage(page: SolfegePageState, action: SolfegePageAction): SolfegePageState {
  if (action.type === 'open') {
    return { session: action.state };
  }
  if (action.type === 'close') {
    return {};
  }
  if (!page.session) {
    return page;
  }
  const session = reduceSolfegeSession(page.session, action);
  // the machine hands back the state itself for an action that changes nothing, and so does the page
  return session === page.session ? page : { session };
}

// The piano run of a sound: its notes, the soft ones at the sing-along guide's gain
function solfegePianoNotes(plan: SolfegeSoundPlan): PianoNote[] {
  return plan.notes.map(({ midi, offsetMs, durationMs, soft }) =>
    soft ? { midi, offsetMs, durationMs, gain: softGuideGain } : { midi, offsetMs, durationMs },
  );
}

const SingInSolfegeExercise = ({
  audio: audioProp,
  random: randomProp,
  initialState,
  phraseCount = solfegePhrasesPerSession,
  range,
  progress,
  updateProgress,
  flush,
}: Props & {
  range: VocalRange;
  progress: LearnProgress;
  updateProgress: (update: LearnProgressUpdate) => void;
  flush: () => Promise<void>;
}) => {
  const audio = audioProp ?? getLearnAudioSession();
  const random = randomProp ?? Math.random;
  const today = useCurrentDate();
  // This module's own choice, off until the singer turns it on (see A session in the header)
  const [singAlongChoice, setSingAlongChoice] = useLocalStorage(LocalStorageKey.SolfegeSingAlong);
  const singAlong = singAlongChoice ?? false;
  const [recorded, setRecorded] = useState<RecordedSession | undefined>(undefined);
  // Met this visit: "Got it, let's start" moves on even when there is nobody to record it for
  const [metSyllables, setMetSyllables] = useState(false);
  const [syllablesReopened, setSyllablesReopened] = useState(false);
  // The take "Hear mine" plays, in memory only; mirrored in a ref for the sound effect
  const [keptTake, setKeptTake] = useState<CaptureSlice | undefined>(undefined);
  const { unlocked } = moduleProgress(progress, LearnModule.SingInSolfege);
  const clef = clefForVoicePart(progress.voicePart ?? voicePartForRange(range));
  // Where a phrase's do may go besides the singer's range: on or near their staff
  const nearStaff = useMemo(() => nearStaffMidiRange(clef), [clef]);
  // The do the syllables' rungs are played from, as Interval Training plays them
  const ladderHomeMidi = useMemo(() => ladderHomeNote(range, LearnLevel.Easy, nearStaff), [nearStaff, range]);

  const [page, dispatch] = useReducer(reduceSolfegePage, { session: initialState });
  const state = page.session;

  const mountedRef = useRef(true);
  const stateRef = useRef(state); // the latest committed state, which the sound effect and ticks read
  const playedSoundIdRef = useRef<number | undefined>(undefined); // the sound request being played
  const recordedRef = useRef(false);
  const previousHomeRef = useRef<number | undefined>(undefined); // the last do of the session before
  const keptTakeRef = useRef<CaptureSlice | undefined>(undefined);
  const slicedTakeRef = useRef<SolfegeSessionState['take']>(undefined); // the take last cut from the capture
  // Bumped when a session closes, so whatever was under way for it is dropped when it lands: a sound
  // reported late, or a Start, Play again or Try again still waiting on the microphone
  const sessionCountRef = useRef(0);

  useEffect(() => {
    stateRef.current = state;
  });

  // Every analysed frame goes to the state machine for the page's life
  useEffect(() => audio.onFrame((frame) => dispatch({ type: 'frame', frame })), [audio]);

  // Leaving the page unaims the detector, silences anything pending, ends the mic track and rests
  // the session, all inside leave(); the capture goes off with it (below). Nothing is recorded.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      keptTakeRef.current = undefined;
      audio.leave();
    };
  }, [audio]);

  // The rolling capture is on while a session is open, up to its summary, and off (dropping what it
  // kept) when the session ends, closes or the page goes
  const capturing = state !== undefined && state.phase !== 'summary';
  useEffect(() => {
    if (!capturing) {
      return;
    }
    audio.setCaptureEnabled(true);
    return () => audio.setCaptureEnabled(false);
  }, [audio, capturing]);

  // A visible page that goes hidden interrupts: a count-in or a take is abandoned for listen, idle
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        dispatch({ type: 'interrupted' });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Plays one request and reports when the singer hears it start (see Sounds in the header)
  const playSound = useCallback(
    (plan: SolfegeSoundPlan, requestId: number) => {
      const session = sessionCountRef.current;
      const stillPlaying = () =>
        mountedRef.current && sessionCountRef.current === session && playedSoundIdRef.current === requestId;
      const atMs = audio.nowMs() + intervalSoundLeadMs;
      let started: Promise<number>;
      if (plan.kind === 'recording') {
        const kept = keptTakeRef.current;
        if (!kept) {
          dispatch({ type: 'stopHearing' }); // nothing kept to play: "Stop" goes back to "Hear mine"
          return;
        }
        started = audio.playRecording(kept.samples, kept.sampleRate, atMs);
      } else {
        started = audio.schedulePianoNotes(solfegePianoNotes(plan), atMs).then((anchorMs) => {
          // the clicks keep time with the notes, from where the run really started
          if (stillPlaying()) {
            plan.clickOffsetsMs.forEach((offsetMs) => audio.scheduleClick(anchorMs + offsetMs));
          }
          return anchorMs;
        });
      }
      void started
        .then((startedMs) => startedMs + audio.heardLagMs())
        .catch((error: unknown) => {
          console.error('Sing in Solfège: a sound could not be played', error);
          return audio.nowMs();
        })
        .then((heardMs) => {
          // Request ids start again from 1 with each session: a report for a closed one is not the next's
          if (!mountedRef.current || sessionCountRef.current !== session) {
            return;
          }
          dispatch({ type: 'soundStarted', requestId, atMs: heardMs });
        });
    },
    [audio],
  );

  // The sound follows the state: each new request is played once, and one that goes away or is
  // replaced is silenced - its piano, its recording and its clicks still to come. The ref is the
  // request being played, which keeps StrictMode's doubled effects from playing it twice.
  const soundId = state?.sound?.id;
  useEffect(() => {
    const playedId = playedSoundIdRef.current;
    if (playedId !== undefined && playedId !== soundId) {
      audio.stopPiano();
      audio.cancelScheduled();
    }
    if (soundId === undefined) {
      playedSoundIdRef.current = undefined;
      return;
    }
    const current = stateRef.current;
    const plan = current && solfegeSoundPlan(current);
    if (playedId === soundId || !plan) {
      return;
    }
    playedSoundIdRef.current = soundId;
    playSound(plan, soundId);
  }, [audio, playSound, soundId]);

  // The estimator follows the note being sung, and is unaimed when nothing is
  const targetMidi = state ? estimatorTargetMidi(state) : undefined;
  useEffect(() => {
    audio.setTargetMidi(targetMidi);
  }, [audio, targetMidi]);

  // The clock drives the sounds, the count-in and the take. A mic found closed while it runs through
  // a count-in or a take interrupts it, as a hidden page does.
  const clockRunning = state !== undefined && solfegeClockRunning(state);
  useAudioClock(audio, clockRunning, (nowMs) => {
    if (stateRef.current?.phase === 'sing' && !audio.isMicOpen()) {
      dispatch({ type: 'interrupted' });
      return;
    }
    dispatch({ type: 'tick', nowMs });
  });

  // Hear mine: the take is cut from the capture once the capture has caught up with the take's end,
  // still on the result and before anything can end the mic, and dropped as the result goes (see the
  // header). A take is cut once: a result returned to after a failure is not cut again from a capture
  // that may have lost it.
  const resultTake = state?.phase === 'result' ? state.take : undefined;
  const takeSpan = state && resultTake ? solfegeTakeSpan(state) : undefined;
  const takeStartMs = takeSpan?.startMs;
  const takeEndMs = takeSpan?.endMs;
  const caughtUp = takeEndMs !== undefined && (state?.clockMs ?? -Infinity) >= takeEndMs + solfegeCaptureCatchUpMs;
  // The capture is the audio session's, outside React, so it is read here rather than in render; the
  // state set from it is what offers "Hear mine", once per take, and the layout effect has it on
  // screen in the same paint as the clock reading that let it be cut.
  useLayoutEffect(() => {
    if (!resultTake) {
      if (keptTakeRef.current !== undefined) {
        keptTakeRef.current = undefined;
        setKeptTake(undefined);
      }
      return;
    }
    if (!caughtUp || slicedTakeRef.current === resultTake) {
      return;
    }
    slicedTakeRef.current = resultTake;
    const kept = keptTakeOf(audio.takeCapture(), takeStartMs, takeEndMs);
    keptTakeRef.current = kept;
    setKeptTake(kept);
  }, [audio, caughtUp, resultTake, takeEndMs, takeStartMs]);

  // The summary is the one point that records the session, on the transition into it, as Interval
  // Training records: inside the updater, reusing its result for the screen, in a layout effect so
  // the summary's first paint already carries the flags
  useLayoutEffect(() => {
    if (state?.phase !== 'summary' || recordedRef.current) {
      return;
    }
    recordedRef.current = true;
    const session = {
      module: LearnModule.SingInSolfege,
      level: state.level,
      score: solfegeSummaryScore(state),
      now: nowOn(today),
    };
    let stored: RecordedSession | undefined;
    updateProgress((current) => {
      stored = recordLearnSession({ progress: current, ...session, playableLevels: selectableLevels });
      return stored.progress;
    });
    // The recorded session is what the summary screen reads; nothing else can produce its flags.
    setRecorded(stored ?? recordLearnSession({ progress, ...session, playableLevels: selectableLevels }));
    void flush();
    audio.leave(); // the session is over: the same teardown as leaving the page
  }, [audio, flush, progress, state, today, updateProgress]);

  // Out of the exercise: back to the Pitch class screen
  const onBack = useLearnExit(solfegeBack().path);

  // The header's arrow in a session: back to the start screen, recording nothing and keeping nothing
  const closeSession = useCallback(() => {
    audio.leave();
    sessionCountRef.current += 1;
    playedSoundIdRef.current = undefined;
    recordedRef.current = false;
    slicedTakeRef.current = undefined;
    keptTakeRef.current = undefined;
    setKeptTake(undefined);
    dispatch({ type: 'close' });
  }, [audio]);

  // The gesture that opens the mic, reporting a refusal instead of throwing
  const openMic = useCallback(async (): Promise<MicFailure | undefined> => {
    try {
      await audio.openMic();
      return undefined;
    } catch (error) {
      return micFailureOf(error);
    }
  }, [audio]);

  const after = recorded?.progress ?? progress;
  // How many sessions the singer has played, the one just recorded included: where the deck goes on;
  // and how many of them at this level qualified, which brings in the later kinds of phrase
  const { sessions: sessionsPlayed, qualifyingSessions } = moduleProgress(after, LearnModule.SingInSolfege);
  const qualifyingPlayed = qualifyingSessions[solfegeLevel] ?? 0;

  // The one way into a session, behind Start and the summary's "Play again": the mic first, then the
  // session's phrases and their dos, and the failure screen if the mic refused. A session closed while
  // the microphone was opening is not opened when it lands.
  const beginSession = useCallback(async () => {
    const sessionCount = sessionCountRef.current;
    const failure = await openMic();
    if (!mountedRef.current || sessionCountRef.current !== sessionCount) {
      return;
    }
    recordedRef.current = false;
    setRecorded(undefined);
    const phrases = pickSolfegePhrases(range, sessionsPlayed, qualifyingPlayed, solfegeLevel, phraseCount);
    const placed = placeSolfegePhrases(range, phrases, {
      random,
      previous: previousHomeRef.current,
      within: nearStaff,
    });
    previousHomeRef.current = placed[placed.length - 1]?.homeMidi;
    const opened = initialSolfegeSessionState({
      level: solfegeLevel,
      phrases: placed,
      guide: singAlong ? 'singAlong' : 'guideFirst',
    });
    dispatch({ type: 'open', state: opened });
    if (failure) {
      dispatch({ type: 'micFailed', failure });
    }
  }, [nearStaff, openMic, phraseCount, qualifyingPlayed, random, range, sessionsPlayed, singAlong]);

  // Try again: the mic is opened again from this tap, then the session resumes
  const onRetry = useCallback(async () => {
    const sessionCount = sessionCountRef.current;
    const failure = await openMic();
    if (!mountedRef.current || sessionCountRef.current !== sessionCount) {
      return; // gone, or the session was closed while the mic opened
    }
    dispatch(failure ? { type: 'micFailed', failure } : { type: 'retry' });
  }, [openMic]);

  // A count-in starts only with the mic open: one that was lost (an interruption) is opened again
  // from the tap, and one that will not open shows the failure screen
  const withMic = useCallback(
    async (action: SolfegeSessionAction) => {
      if (audio.isMicOpen()) {
        dispatch(action);
        return;
      }
      const sessionCount = sessionCountRef.current;
      const failure = await openMic();
      if (!mountedRef.current || sessionCountRef.current !== sessionCount) {
        return;
      }
      dispatch(failure ? { type: 'micFailed', failure } : action);
    },
    [audio, openMic],
  );

  // A rung of the syllables: its note from the ladder's do, never once the page has gone
  const onPlaySyllable = useCallback(
    (degree: number) => {
      const sessionCount = sessionCountRef.current;
      const stillWanted = () => mountedRef.current && sessionCountRef.current === sessionCount;
      playSoloNote(audio, intervalNoteMidi(ladderHomeMidi, degree), stillWanted).catch((error: unknown) =>
        console.error('Sing in Solfège: a syllable could not be played', error),
      );
    },
    [audio, ladderHomeMidi],
  );

  // "Got it, let's start": recorded the first time, and only the first; reopened, it goes back
  const onSyllablesDone = useCallback(() => {
    if (syllablesReopened) {
      setSyllablesReopened(false);
      return;
    }
    updateProgress((current) => markSyllablesMet(current, nowOn(today)));
    void flush();
    setMetSyllables(true);
  }, [flush, syllablesReopened, today, updateProgress]);

  const onToggleSingAlong = useCallback((value: boolean) => setSingAlongChoice(value), [setSingAlongChoice]);
  const onSing = useCallback(() => void withMic({ type: 'sing' }), [withMic]);
  const onStartOver = useCallback(() => void withMic({ type: 'startOver' }), [withMic]);
  const onSingAgain = useCallback(() => void withMic({ type: 'singAgain' }), [withMic]);
  const onPlayAgain = useCallback(() => dispatch({ type: 'hearAgain' }), []);
  const onNext = useCallback(() => dispatch({ type: 'next' }), []);
  const onHearGuide = useCallback(() => dispatch({ type: 'hearGuide' }), []);
  // "Hear mine" names the part of the take it will play, on the take's scale; "Stop" needs nothing
  const onHearMine = useCallback(() => {
    const current = stateRef.current;
    const kept = keptTakeRef.current;
    const startMs = current?.take?.startMs;
    if (!current || isSolfegeHearingMine(current) || !kept || startMs === undefined) {
      dispatch({ type: 'hearMine' });
      return;
    }
    const clipStartMs = kept.startMs - startMs;
    const clip = { startMs: clipStartMs, endMs: clipStartMs + (kept.samples.length * 1000) / kept.sampleRate };
    dispatch({ type: 'hearMine', clip });
  }, []);
  // A syllable tapped on the listen screen: its note alone, the session unchanged (see the header)
  const onPlayNote = useCallback(
    (index: number) => {
      const current = stateRef.current;
      if (current?.phase !== 'listen' || isSolfegePhrasePlaying(current)) {
        return;
      }
      const { phrase, homeMidi } = currentSolfegePhrase(current);
      const note = phrase.notes[index];
      if (!note) {
        return;
      }
      const sessionCount = sessionCountRef.current;
      const stillWanted = () => mountedRef.current && sessionCountRef.current === sessionCount;
      playSoloNote(audio, solfegeNoteMidi(homeMidi, note), stillWanted).catch((error: unknown) =>
        console.error('Sing in Solfège: a note could not be played', error),
      );
    },
    [audio],
  );

  const showSyllables = !state && (syllablesReopened || (needsSyllables(progress) && !metSyllables));
  const phase: SolfegePagePhase = state?.phase ?? (showSyllables ? 'syllables' : 'start');
  const moduleName = learnModuleName(LearnModule.SingInSolfege);
  const title = `${moduleName} · ${learnLevelName(state?.level ?? solfegeLevel)}`;
  const onHeaderBack = state
    ? closeSession
    : syllablesReopened
      ? () => setSyllablesReopened(false)
      : () => void onBack();

  return (
    <OuterPageContent id={singInSolfegePageId} data-phase={phase}>
      <Header title={title} onBack={onHeaderBack} />
      {phase === 'syllables' && <MeetSyllablesScreen onPlay={onPlaySyllable} onDone={onSyllablesDone} />}
      {phase === 'start' && (
        <SolfegeStartScreen
          level={solfegeLevel}
          unlocked={unlocked}
          selectable={selectableLevels}
          singAlong={singAlong}
          onSelectLevel={() => undefined} // Easy is the only level to select
          onToggleSingAlong={onToggleSingAlong}
          onOpenSyllables={() => setSyllablesReopened(true)}
          onStart={() => void beginSession()}
        />
      )}
      {(state?.phase === 'listen' || state?.phase === 'sing' || state?.phase === 'result') && (
        <SolfegePhraseScreen
          state={state}
          clef={clef}
          canHearMine={keptTake !== undefined}
          onSing={onSing}
          onPlayAgain={onPlayAgain}
          onStartOver={onStartOver}
          onNext={onNext}
          onHearMine={onHearMine}
          onHearGuide={onHearGuide}
          onSingAgain={onSingAgain}
          onPlayNote={onPlayNote}
        />
      )}
      {state?.phase === 'failed' && (
        <SingingFailureScreen
          failure={state.failure ?? 'unknown'}
          idPrefix={solfegeIdPrefix}
          backLabel={solfegeBack().label}
          onRetry={() => void onRetry()}
          onBack={() => void onBack()}
        />
      )}
      {state?.phase === 'summary' && (
        <LearnSummaryScreen
          idPrefix={solfegeIdPrefix}
          moduleName={moduleName}
          level={state.level}
          {...solfegeSummaryProps(state)}
          module={moduleProgress(after, LearnModule.SingInSolfege)}
          dayStreak={currentStreak(after, today)}
          isNewBest={recorded?.isNewBest ?? false}
          unlockedLevel={playableUnlock(recorded?.unlockedLevel)}
          onPlayAgain={() => void beginSession()}
          backLabel={solfegeBack().label}
          onBack={() => void onBack()}
        />
      )}
    </OuterPageContent>
  );
};
